import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Field,
  Input,
  RadioGroup,
  Select,
  SliderModal,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@see-ui/core';
import React, { useEffect, useState } from 'react';
import { useGlobalStore } from '@services/store/global-store';
import * as service from '@services/api/member-pharmacy-service';

const AddPaymentMethodsSlider: React.FC<{
  onClose: () => void;
  onUpdatePaymentMethods: any;
}> = ({ onClose, onUpdatePaymentMethods }) => {
  const { memberDetails } = useGlobalStore((state) => state);
  const [form, setForm] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [activeTab, setActiveTab] = useState('tab1');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleChange = (name: string, value: string) => {
    setForm((prev: any) => ({ ...prev, [name]: value }));
    setErrors((prev: any) => ({ ...prev, [name]: undefined }));
  };

  const validateCard = () => {
    const requiredFields = [
      'name',
      'address',
      'zipCode',
      'city',
      'state',
      'cardNumber',
      'expMonth',
      'expYear',
      'cvvNumber'
    ];
    const newErrors: any = {};
    requiredFields.forEach((field) => {
      if (!form[field]) newErrors[field] = 'Required';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (activeTab === 'tab2' && !form.bankAccountType) {
      setForm((prev: any) => ({
        ...prev,
        bankAccountType: 'PERSONALCHECKING'
      }));
    }
  }, [activeTab]);

  const validateBank = () => {
    const requiredFields = [
      'bankAccountType',
      'bankRoutingNumber',
      'accountNumber',
      'reEnterAccountNumber',
      'accountHolderName',
      'addressOnAccount',
      'zipCode',
      'city',
      'state'
    ];
    const newErrors: any = {};
    requiredFields.forEach((field) => {
      if (!form[field]) newErrors[field] = 'Required';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    setErrorMessage('');
    const valid = activeTab === 'tab1' ? validateCard() : validateBank();
    if (!valid) return;
    if (activeTab === 'tab1') {
      //Adding new card
      handleCardAdd();
    } else {
      //Adding new bank account
      handleBankAccountAdd();
    }
  };

  const handleCardAdd = async () => {
    let cardSubType = '';
    if (/^5[1-5]/.test(form.cardNumber)) {
      cardSubType = 'MASTERCARD';
    } else if (/^4/.test(form.cardNumber)) {
      cardSubType = 'VISA';
    }
    const request = {
      action: 'ADD',
      paymentMethod: {
        card: {
          cardHolderName: form.name,
          cardNumber: form.cardNumber,
          cardType: 'CREDITDEBITCARD',
          cardSubType,
          expirationMonth: form.expMonth,
          expirationYear: form.expYear,
          integrityCheck: '7870df9594830a89', //TODO: Replace with actual integrity check value
          cardSecValue: form.cvvNumber,
          keyId: 'e5f6f06f', //TODO: Replace with actual key ID
          phaseId: '1' //TODO: Replace with actual phase ID
        },
        isDefaultPaymentMethod: !!form.isDefaultPaymentMethod,
        address: {
          addressLine1: form.address,
          city: form.city,
          state: form.state,
          zipCode: form.zipCode,
          country: 'US'
        }
      }
    };
    const req = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      updateRequest: request
    };
    const updateResp = await service.addUpdatePaymentMethod(req);
    if (updateResp?.data) {
      const getPaymentMethodResp = await service.getPaymentMethod({
        hcid: memberDetails?.member?.memberPolicy?.hcid
      });
      if (getPaymentMethodResp?.data) {
        onUpdatePaymentMethods(getPaymentMethodResp.data?.paymentMethods);
      }
      onClose();
    } else {
      console.log('Failed to add payment method');
      setErrorMessage(
        `Sorry, looks like something isn't working. Please give us some time to fix it. Then, try again.`
      );
    }
  };

  const handleBankAccountAdd = async () => {
    const request = {
      action: 'ADD',
      paymentMethod: {
        bankAccount: {
          accountHolderName: form.accountHolderName,
          routingNumber: form.bankRoutingNumber,
          accountNumber: form.accountNumber,
          accountType: 'BANKACCOUNT',
          accountSubType: form.bankAccountType,
          bankName: 'BANK N.A.,' //TODO
        },
        isDefaultPaymentMethod: false,
        address: {
          addressLine1: form.addressOnAccount,
          addressLine2: form.addressLine2 || '',
          city: form.city,
          state: form.state,
          zipCode: form.zipCode
        }
      }
    };

    const req = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      updateRequest: request
    };
    const updateResp = await service.addUpdatePaymentMethod(req);
    if (updateResp?.data) {
      const getPaymentMethodResp = await service.getPaymentMethod({
        hcid: memberDetails?.member?.memberPolicy?.hcid
      });
      if (getPaymentMethodResp?.data) {
        onUpdatePaymentMethods(getPaymentMethodResp.data?.paymentMethods);
      }
      onClose();
    } else {
      console.log('Failed to add payment method');
      setErrorMessage(
        `Sorry, looks like something isn't working. Please give us some time to fix it. Then, try again.`
      );
    }
  };

  return (
    <>
      <SliderModal
        direction="right"
        labelAriaCloseBtn="Close Modal"
        onClose={onClose}
        open
      >
        <SliderModal.Body>
          <div>
            <h2 className="mb-6"> Add a New Payment Method</h2>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex mb-4">
                <TabsTrigger className="flex-1" value="tab1" variant="button">
                  Credit and Debit Cards
                </TabsTrigger>
                <TabsTrigger className="flex-1" value="tab2" variant="button">
                  Bank Accounts
                </TabsTrigger>
              </TabsList>
              <TabsContent value="tab1">
                <CardTabContent
                  form={form}
                  errors={errors}
                  onChange={handleChange}
                />
              </TabsContent>
              <TabsContent value="tab2">
                <BankTabContent
                  form={form}
                  errors={errors}
                  onChange={handleChange}
                />
              </TabsContent>
            </Tabs>
          </div>
          {errorMessage && (
            <Alert variant="error" className="mt-4">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
        </SliderModal.Body>
        <SliderModal.Footer>
          <Button variant="secondary" className="mr-5" onClick={onClose}>
            Cancel
          </Button>
          <Button className="mr-5" onClick={handleAdd}>
            Add
          </Button>
        </SliderModal.Footer>
      </SliderModal>
    </>
  );
};

export default AddPaymentMethodsSlider;

const CardTabContent: React.FC<{ form: any; errors: any; onChange: any }> = ({
  form,
  errors,
  onChange
}) => (
  <>
    <Field
      label={
        <span className="font-semibold">
          Cardholder’s Name (enter name as it appears on the card)
        </span>
      }
      name="name"
      required
      validationState={errors.name ? 'error' : undefined}
      helperText={errors.name ? 'Cardholder’s Name is required' : ''}
    >
      <Input
        value={form.name || ''}
        onChange={(e) => onChange('name', e.target.value)}
        type="text"
      />
    </Field>
    <p className="mb-4"></p>
    <Field
      label={
        <span className="font-semibold">
          Address on Account (Cardholder’s billing address)
        </span>
      }
      name="address"
      required
      validationState={errors.address ? 'error' : undefined}
      helperText={errors.address ? 'Address is required' : ''}
    >
      <Input
        value={form.address || ''}
        onChange={(e) => onChange('address', e.target.value)}
        type="text"
      />
    </Field>
    <p className="mb-4"></p>
    <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
      <div>
        <Field
          label={<span className="font-semibold">ZIP Code</span>}
          name="zipCode"
          required
          validationState={errors.zipCode ? 'error' : undefined}
          helperText={errors.zipCode ? 'ZIP Code is required' : ''}
        >
          <Input
            value={form.zipCode || ''}
            onChange={(e) => onChange('zipCode', e.target.value)}
            type="text"
          />
        </Field>
      </div>
      <div>
        <Field
          label={<span className="font-semibold">City</span>}
          name="city"
          required
          validationState={errors.city ? 'error' : undefined}
          helperText={errors.city ? 'City is required' : ''}
        >
          <Input
            value={form.city || ''}
            onChange={(e) => onChange('city', e.target.value)}
            type="text"
          />
        </Field>
      </div>
      <div>
        <Field
          label={<span className="font-semibold">State</span>}
          name="state"
          required
          validationState={errors.state ? 'error' : undefined}
          helperText={errors.state ? 'State is required' : ''}
        >
          <Input
            value={form.state || ''}
            onChange={(e) => onChange('state', e.target.value)}
            type="text"
          />
        </Field>
      </div>
    </div>
    <Field
      label={<span className="font-semibold">Card Number</span>}
      name="cardNumber"
      required
      validationState={errors.cardNumber ? 'error' : undefined}
      helperText={errors.cardNumber ? 'Card Number is required' : ''}
    >
      <Input
        value={form.cardNumber || ''}
        onChange={(e) => onChange('cardNumber', e.target.value)}
        type="text"
      />
    </Field>
    <p className="mb-4"></p>
    <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
      <div>
        <Field
          name="expMonth"
          required
          label={<span className="font-semibold">Month</span>}
          validationState={errors.expMonth ? 'error' : undefined}
          helperText={errors.expMonth ? 'Month is required' : ''}
        >
          <Select
            name="expMonth"
            value={form.expMonth || ''}
            onValueChange={(value: string) => onChange('expMonth', value)}
            options={Array.from({ length: 12 }, (_, i) => ({
              label: (i + 1).toString().padStart(2, '0'),
              value: (i + 1).toString().padStart(2, '0')
            }))}
            required
          />
        </Field>
      </div>
      <div>
        <Field
          name="expYear"
          required
          label={<span className="font-semibold">Year</span>}
          validationState={errors.expYear ? 'error' : undefined}
          helperText={errors.expYear ? 'Year is required' : ''}
        >
          <Select
            name="expYear"
            value={form.expYear || ''}
            onValueChange={(value: string) => onChange('expYear', value)}
            options={Array.from({ length: 21 }, (_, i) => {
              const year = 2026 + i;
              return { label: year.toString(), value: year.toString() };
            })}
            required
          />
        </Field>
      </div>
    </div>

    <Field
      label={<span className="font-semibold">CVV Number</span>}
      name="cvvNumber"
      required
      validationState={errors.cvvNumber ? 'error' : undefined}
      helperText={errors.cvvNumber ? 'CVV Number is required' : ''}
    >
      <Input
        value={form.cvvNumber || ''}
        onChange={(e) => onChange('cvvNumber', e.target.value)}
        type="text"
      />
    </Field>
    <p className="mb-4"></p>
    <Checkbox
      size="sm"
      label="Make this the primary payment method for all your pharmacy orders"
      name="isDefaultPaymentMethod"
      checked={!!form.isDefaultPaymentMethod}
      onCheckedChange={(checked: boolean) =>
        onChange('isDefaultPaymentMethod', checked)
      }
    />
  </>
);

const BankTabContent: React.FC<{ form: any; errors: any; onChange: any }> = ({
  form,
  errors,
  onChange
}) => (
  <>
    <p className="mb-4">
      Select a type of bank account. Bank payments from checking or savings
      accounts must be made from a U.S. bank.
    </p>
    <div className="mb-6 ">
      <div className="flex gap-6">
        <RadioGroup
          value={form.bankAccountType || 'PERSONALCHECKING'}
          options={[
            { label: 'Personal Checking', value: 'PERSONALCHECKING' },
            { label: 'Personal Savings', value: 'PERSONALSAVINGS' },
            { label: 'Business Checking', value: 'BUSINESSCHECKING' },
            { label: 'Business Savings', value: 'BUSINESSSAVINGS' }
          ]}
          name="bankAccountType"
          className="grid grid-cols-2 gap-6"
          onValueChange={(value: string) => onChange('bankAccountType', value)}
        />
      </div>
    </div>
    <Field
      label={<span className="font-semibold">Bank Routing Number</span>}
      name="bankRoutingNumber"
      required
      validationState={errors.bankRoutingNumber ? 'error' : undefined}
      helperText={
        errors.bankRoutingNumber ? 'Bank Routing Number is required' : ''
      }
    >
      <Input
        data-testid="bank-routing-number-input"
        data-analytics="BankRoutingNumber"
        type="text"
        value={form.bankRoutingNumber || ''}
        onChange={(e) => onChange('bankRoutingNumber', e.target.value)}
      />
    </Field>
    <p className=" mt-4"></p>
    <Field
      label={<span className="font-semibold">Account Number</span>}
      name="accountNumber"
      required
      validationState={errors.accountNumber ? 'error' : undefined}
      helperText={errors.accountNumber ? 'Account Number is required' : ''}
    >
      <Input
        data-testid="account-number-input"
        data-analytics="AccountNumber"
        type="text"
        value={form.accountNumber || ''}
        onChange={(e) => onChange('accountNumber', e.target.value)}
      />
    </Field>
    <p className=" mt-4"></p>
    <Field
      label={<span className="font-semibold">Re-enter Account Number</span>}
      name="reEnterAccountNumber"
      required
      validationState={errors.reEnterAccountNumber ? 'error' : undefined}
      helperText={
        errors.reEnterAccountNumber ? 'Re-enter Account Number is required' : ''
      }
    >
      <Input
        data-testid="re-enter-account-number-input"
        data-analytics="ReEnterAccountNumber"
        type="text"
        value={form.reEnterAccountNumber || ''}
        onChange={(e) => onChange('reEnterAccountNumber', e.target.value)}
      />
    </Field>
    <p className=" mt-4"></p>
    <Field
      label={<span className="font-semibold">Account Holder’s Name</span>}
      name="accountHolderName"
      required
      validationState={errors.accountHolderName ? 'error' : undefined}
      helperText={
        errors.accountHolderName ? 'Account Holder’s Name is required' : ''
      }
    >
      <Input
        data-testid="account-holder-name-input"
        data-analytics="AccountHolderName"
        type="text"
        value={form.accountHolderName || ''}
        onChange={(e) => onChange('accountHolderName', e.target.value)}
      />
    </Field>
    <p className=" mt-4"></p>
    <Field
      label={
        <span className="font-semibold">
          Address on Account (Address associated with bank account)
        </span>
      }
      name="addressOnAccount"
      required
      validationState={errors.addressOnAccount ? 'error' : undefined}
      helperText={
        errors.addressOnAccount ? 'Address on Account is required' : ''
      }
    >
      <Input
        data-testid="address-on-account-input"
        data-analytics="AddressOnAccount"
        type="text"
        value={form.addressOnAccount || ''}
        onChange={(e) => onChange('addressOnAccount', e.target.value)}
      />
    </Field>
    <p className="mb-2"></p>
    <div className="grid grid-cols-3 md:grid-cols-3 gap-4">
      <div>
        <Field
          label={<span className="font-semibold">ZIP Code</span>}
          name="zipCode"
          required
          validationState={errors.zipCode ? 'error' : undefined}
          helperText={errors.zipCode ? 'ZIP Code is required' : ''}
        >
          <Input
            name="zipCode"
            value={form.zipCode || ''}
            onChange={(e) => onChange('zipCode', e.target.value)}
          />
        </Field>
      </div>
      <div>
        <Field
          label={<span className="font-semibold">City</span>}
          name="city"
          required
          validationState={errors.city ? 'error' : undefined}
          helperText={errors.city ? 'City is required' : ''}
        >
          <Input
            name="city"
            value={form.city || ''}
            onChange={(e) => onChange('city', e.target.value)}
          />
        </Field>
      </div>
      <div>
        <Field
          label={<span className="font-semibold">State</span>}
          name="state"
          required
          validationState={errors.state ? 'error' : undefined}
          helperText={errors.state ? 'State is required' : ''}
        >
          <Input
            name="state"
            value={form.state || ''}
            onChange={(e) => onChange('state', e.target.value)}
          />
        </Field>
      </div>
    </div>
    <p>
      This bank account will be used for your payment as of today,{' '}
      {new Date().toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })}
      .
    </p>
  </>
);
